/**
 * Clients and onboarding (features.onboarding). Office reads and edits clients and site contacts under RLS;
 * welcome packs, signed PDFs and written job confirmations go through the API's /admin routes.
 */
import { adminBlob, adminFetch } from "../../lib/api";
import { PREVIEW } from "../../lib/preview";
import { supabase } from "../../lib/supabase";

const previewOnly = (): never => {
  throw new Error("Clients are not available in preview mode.");
};

export type OnboardingStatus = "not_sent" | "details_requested" | "opened" | "active";

export interface Client {
  id: string; name: string; legal_name: string | null; trading_name: string | null; abn: string | null; contact_name: string | null;
  email: string | null; phone: string | null; billing_address: string | null; accounts_contact_name: string | null; accounts_email: string | null;
  invoice_email: string | null; po_required: boolean; payment_terms_days: number; onboarding_status: OnboardingStatus;
  details_verified_at: string | null; notes: string | null; created_at: string; updated_at: string;
}
export interface WelcomeRequest {
  id: string; customer_id: string; recipient_name: string; recipient_email: string; payment_terms_days: number; status: "sent" | "opened" | "completed" | "expired" | "cancelled";
  expires_at: string; sent_at: string; opened_at: string | null; completed_at: string | null; cancelled_at: string | null;
}
export interface AccountAgreement {
  id: string; customer_id: string; terms_version_id: string; signer_name: string; signer_position: string; company_legal_name: string;
  payment_terms_days: number; signed_at: string; signed_at_local: string;
}
export interface SiteContactRow { id: string; customer_id: string; name: string; mobile: string | null; email: string | null; role: string | null }

export interface ClientSummary extends Client {
  onboarding_requests: WelcomeRequest[];
  account_agreements: Pick<AccountAgreement, "id" | "terms_version_id" | "signed_at">[];
}
export interface ClientDetail extends ClientSummary {
  customer_site_contacts: SiteContactRow[];
  account_agreements: AccountAgreement[];
  jobs: { id: string; reference: string; title: string; status: string }[];
  quotes: { id: string; quote_number: string; status: string; job_title: string | null; created_at: string }[];
}

/** What the office sees as the client's status, including the derived "terms out of date" and "link expired". */
export type ClientState = OnboardingStatus | "terms_out_of_date" | "link_expired";
export function clientState(c: ClientSummary, latestTermsId: string | null, now = Date.now()): ClientState {
  if (c.onboarding_status === "active") {
    const signed = c.account_agreements.some((a) => a.terms_version_id === latestTermsId);
    return signed || !latestTermsId ? "active" : "terms_out_of_date";
  }
  const live = c.onboarding_requests.find((r) => r.status === "sent" || r.status === "opened");
  if (live && Date.parse(live.expires_at) < now) return "link_expired";
  return c.onboarding_status;
}
export const STATE_LABEL: Record<ClientState, string> = {
  not_sent: "Not sent",
  details_requested: "Details requested",
  opened: "Opened",
  active: "Active, terms signed",
  terms_out_of_date: "Terms out of date",
  link_expired: "Link expired",
};

const SUMMARY = "*, onboarding_requests(id, customer_id, recipient_name, recipient_email, payment_terms_days, status, expires_at, sent_at, opened_at, completed_at, cancelled_at), account_agreements(id, terms_version_id, signed_at)";

export async function listClients(): Promise<ClientSummary[]> {
  if (PREVIEW) return [];
  const { data, error } = await supabase.from("customers").select(SUMMARY).order("name").limit(500);
  if (error) throw error;
  return data as ClientSummary[];
}

export async function getClient(id: string): Promise<ClientDetail | null> {
  if (PREVIEW) return previewOnly();
  const { data, error } = await supabase
    .from("customers")
    .select(
      "*, onboarding_requests(id, customer_id, recipient_name, recipient_email, payment_terms_days, status, expires_at, sent_at, opened_at, completed_at, cancelled_at), " +
        "account_agreements(id, customer_id, terms_version_id, signer_name, signer_position, company_legal_name, payment_terms_days, signed_at, signed_at_local), " +
        "customer_site_contacts(id, customer_id, name, mobile, email, role), jobs(id, reference, title, status), quotes(id, quote_number, status, job_title, created_at)",
    )
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const c = data as unknown as ClientDetail;
  c.onboarding_requests = [...c.onboarding_requests].sort((a, b) => b.sent_at.localeCompare(a.sent_at));
  c.account_agreements = [...c.account_agreements].sort((a, b) => b.signed_at.localeCompare(a.signed_at));
  return c;
}

/** The newest published terms version, or null when none is published yet. */
export async function latestTermsId(): Promise<string | null> {
  if (PREVIEW) return null;
  const { data, error } = await supabase.from("terms_versions").select("id").order("published_at", { ascending: false }).limit(1);
  if (error) throw error;
  return (data?.[0]?.id as string | undefined) ?? null;
}

let orgPromise: Promise<string> | null = null;
async function organisationId(): Promise<string> {
  orgPromise ??= (async () => {
    const { data: s } = await supabase.auth.getSession();
    const { data, error } = await supabase.from("profiles").select("organisation_id").eq("id", s.session?.user.id ?? "").single();
    if (error) throw error;
    return data.organisation_id as string;
  })();
  return orgPromise.catch((e) => {
    orgPromise = null;
    throw e;
  });
}

export interface NewClient { name: string; contact_name: string; email: string | null; phone: string | null; payment_terms_days?: number; notes?: string | null }

export async function createClient(c: NewClient): Promise<string> {
  if (PREVIEW) return previewOnly();
  const { data, error } = await supabase.from("customers").insert({ ...c, email: c.email?.trim().toLowerCase() || null, organisation_id: await organisationId() }).select("id").single();
  if (error) throw error;
  return data.id as string;
}

export type ClientFields = Partial<Pick<Client, "name" | "legal_name" | "trading_name" | "abn" | "contact_name" | "email" | "phone" | "billing_address" | "accounts_contact_name" | "accounts_email" | "invoice_email" | "po_required" | "payment_terms_days" | "notes">>;

export async function updateClient(id: string, fields: ClientFields): Promise<void> {
  if (PREVIEW) return previewOnly();
  const { error } = await supabase.from("customers").update(fields).eq("id", id);
  if (error) throw error;
}

export async function addSiteContact(customerId: string, c: { name: string; mobile: string | null; email: string | null; role: string | null }): Promise<void> {
  if (PREVIEW) return previewOnly();
  const { error } = await supabase.from("customer_site_contacts").insert({ ...c, customer_id: customerId, organisation_id: await organisationId() });
  if (error) throw error;
}

export async function removeSiteContact(id: string): Promise<void> {
  if (PREVIEW) return previewOnly();
  const { error } = await supabase.from("customer_site_contacts").delete().eq("id", id);
  if (error) throw error;
}

export async function sendWelcomePack(customerId: string, body: { recipientName?: string; recipientEmail?: string; paymentTermsDays?: number }): Promise<{ link: string; emailed: boolean; emailError: string | null; expiresAt: string }> {
  if (PREVIEW) return previewOnly();
  return adminFetch(`/customers/${customerId}/welcome`, { method: "POST", body: JSON.stringify(body) });
}

export async function cancelWelcomePack(requestId: string): Promise<void> {
  if (PREVIEW) return previewOnly();
  await adminFetch(`/onboarding-requests/${requestId}/cancel`, { method: "POST", body: "{}" });
}

export async function accountAgreementPdf(id: string): Promise<Blob> {
  if (PREVIEW) return previewOnly();
  return adminBlob(`/account-agreements/${id}/pdf`, {}, { timeoutMs: 60_000 });
}

/** Make a client from a website lead (the office checks the details on the client record). */
export async function clientFromLead(lead: { name: string; company: string | null; email: string | null; phone: string | null }): Promise<string> {
  return createClient({ name: lead.company?.trim() || lead.name, contact_name: lead.name, email: lead.email, phone: lead.phone });
}

/* ---------- written job confirmation ---------- */

export async function recordJobConfirmation(jobId: string, note: string, file: File | null): Promise<{ termsCurrent: boolean; shiftsConfirmed: number }> {
  if (PREVIEW) return previewOnly();
  const form = new FormData();
  form.set("note", note);
  if (file) form.set("file", file);
  return adminFetch(`/jobs/${jobId}/client-confirmation`, { method: "POST", body: form }, { timeoutMs: 120_000 });
}

export async function jobConfirmationFile(jobId: string): Promise<Blob> {
  if (PREVIEW) return previewOnly();
  return adminBlob(`/jobs/${jobId}/client-confirmation/file`, {}, { timeoutMs: 60_000 });
}
